/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

require('~/bootstrap');

window.Vue = require('vue').default;
require('vue-axios-interceptors');
window.axios = require('axios');
window.axios.interceptors.response.use(
    function (response) {
        return response;
    }, function (error) {
        if (error.response.status === 403) {
            Vue.prototype.$eventBus.$emit('unauthorised', true);
        } else {
            return Promise.reject(error);
        }
    }
);
Vue.prototype.$eventBus = new Vue();

import VueRouter from "vue-router";
import XDS from "@hyperionx/xds";
import VueClip from "vue-clip";
import VueScrollTo from 'vue-scrollto';
import VueGtag from "vue-gtag";
import Axios from "axios";
import {
    setupCache,
    buildWebStorage,
    defaultKeyGenerator,
    defaultHeaderInterpreter
} from 'axios-cache-interceptor';

Vue.prototype.$httpCached = setupCache(
    Axios.create({
        headers: {
            Authorization: 'Bearer ' + (JSON.parse(localStorage.getItem('hyx-auth') || '{}')).access_token,
            'Authorization-Provider': localStorage.getItem('hyx-auth-provider'),
        }
    }),
    {
        storage: buildWebStorage(sessionStorage, 'axios-cache:'),
        generateKey: defaultKeyGenerator,
        headerInterpreter: defaultHeaderInterpreter,
        ttl: 1000 * 60 * 60 * 12,
        interpretHeader: false,
    }
);
Vue.prototype.$httpCached.interceptors.response.use(
    function (response) {
        return response;
    }, function (error) {
        if (error.response.status === 401) {
            return axios.request(error.config); // Replay request uncached to get refresh token etc via interceptors
        }

        return Promise.reject(error);
    }
);

import { createPinia, PiniaVuePlugin } from 'pinia';

Vue.use(PiniaVuePlugin)
const pinia = createPinia()

Vue.use(VueRouter);
Vue.use(VueScrollTo);
Vue.use(VueClip);
Vue.use(XDS, {
    theme: {
        'primary': '#173F35',
        'secondary': '#FFBF3F',
        'accent': '#8D4D66',
        'error': '#DC000F',
        'info': '#4A9DD1',
        'success': '#0DA20D',
        'warning': '#FAAA00',
        'cancel': '#E1E4EB',
        'red': '#FF585F',
        'orange': '#FFA700',
        'yellow': '#FFCE00',
        'green': '#52C76B',
        'teal': '#00F8D2',
        'blue': '#0777FF',
        'plum': '#8D4D66',
        'ocean': '#1E9FD7',
        'olive': '#41807F',
        'pistachio': '#E0EC89',
        'offWhite': '#F6F6F6',
        'remove': '#FF585F',
    },
    iconfont: "fa"
});

import Default from '~/layouts/Default.vue';
import Login from '~/layouts/Login.vue';
import Standard from "~/layouts/Standard";

Vue.component('app', require('~/App.vue').default);

// Layouts
Vue.component('default-layout', Default);
Vue.component('standard-layout', Standard);
Vue.component('login-layout', Login);

//Components
Vue.component('field-group', require('~/components/Shared/UIComponents/FieldGroup').default);
Vue.component('question-section', require('~/components/Shared/UIComponents/QuestionSection').default);
Vue.component('x-numerical-text-field', require('~/../../../resources/assets/js/components/Shared/UIComponents/XNumericalTextField').default);
Vue.component('x-currency-text-field', require('~/../../../packages/HyperionX/UIComponents/XCurrencyTextField').default);
Vue.component('error-messages', require('~/components/Shared/Interceptors/ErrorMessages').default);


import moment from "moment";
Vue.prototype.$moment = moment;

// Load up filters
import filters from '~/filters/filters';
Vue.use(filters);

// Routes
import routes from '~/routes/routes';
let router = new VueRouter({
    mode: 'history',
    routes,
});

// Setup Google Analytics
window.activateGoogleAnalytics = function() {
    Vue.use(VueGtag, {
        config: {
            id: "UA-151752887-2"
        }
    }, router);
}

const data = {
    info: {},
    user: {},
    scopes: [],
    role: '',
    notifications: null,
    jobRoles: []
};

//Setup auth service
import {HyperionAuth} from "@hyperionx/hyperion-x-auth";
Vue.prototype.$hyperionAuth = new HyperionAuth();

// Creates a `nextMiddleware()` function which not only
// runs the default `next()` callback but also triggers
// the subsequent Middleware function.
const nextFactory = (context, middleware, index) => {
    const subsequentMiddleware = middleware[index];
    // If no subsequent Middleware exists,
    // the default `next()` callback is returned.
    if (!subsequentMiddleware) {
        return context.next;
    }

    return (...parameters) => {
        // Run the default Vue Router `next()` callback first.
        context.next(...parameters);
        // Then run the subsequent Middleware with a new
        // `nextMiddleware()` callback.
        const nextMiddleware = nextFactory(context, middleware, index + 1);
        subsequentMiddleware({
            ...context,
            next: nextMiddleware
        });
    };
}

router.beforeEach(async (to, from, next) => {
    await router.app.$hyperionAuth.initialize();

    const routeScopes = to.meta.scopes;

    if (!app.hasOwnProperty('user') || !app.user.hasOwnProperty('permissions')) {
        if (!['login'].includes(to.name)) {
            await app.loadData();
        }
    }

    if (!localStorage.getItem('activities-framework-type') && router.app.$hyperionAuth.isAuthenticated()) {
        await axios.get('/api/activities/framework/type').then(response => {
            localStorage.setItem('activities-framework-type', response.data);
        })
    }

    // Unprotected route
    if (typeof routeScopes === 'undefined') {
        if (to.meta.middleware) {
            const middleware = Array.isArray(to.meta.middleware) ? to.meta.middleware : [to.meta.middleware];
            const context = {
                from,
                next,
                router,
                to
            };
            const nextMiddleware = nextFactory(context, middleware, 1);

            return middleware[0]({
                ...context,
                next: nextMiddleware
            });
        }

        return next();
    }

    // Access all Scope
    routeScopes.push('*');

    // Get user scopes
    const userScopes = router.app.$hyperionAuth.getUserScopes();
    const hasAccess = routeScopes.filter(routeScope => userScopes.includes(routeScope)).length > 0;

    if (hasAccess) {
        return next();
    }

    next({path: '/unauthorised'})
});


const app = new Vue({
    el: '#application',
    router,
    pinia,
    data: data,
    async created() {
        await this.$hyperionAuth.initialiseEnvironment(this.unauthenticated, this.unauthorised, this.redirectionToUpdateExpiredPassword);
    },
    methods: {
        unauthenticated() {
            this.$router.push('/login');
        },
        unauthorised() {
            this.$router.push('/unauthorised');
        },
        redirectionToUpdateExpiredPassword(redirectTo) {
            this.$router.push({name: redirectTo});
        },
        async loadData() {
            this.$root.user = (await axios.get(this.$root.api('user'), {responseType: 'json'})).data.data;

            await this.$httpCached.get(this.$root.api(`application`)).then(response => {
                this.$root.application =  response.data;
            })

            await this.$httpCached.get(this.$root.api('contract-roles')).then(response => {
                this.$root.contact_roles = response.data.data;
            })

            await this.$httpCached.get(this.$root.api('currency')).then(response => {
                this.$root.currencies = response.data.data;
            })

            await this.$httpCached.get(this.$root.api("country")).then((res) => {
                this.$root.countries = res.data.data;
            })
        },
        api(path) {
            console.log("API: " + path + " ==> " + this.$hyperionAuth.getEnvironment()['MIX_APP_API_DOMAIN'] + path);
            return this.$hyperionAuth.getEnvironment()['MIX_APP_API_DOMAIN'] + path;
        },
        env(key) {
            return this.$hyperionAuth.getEnvironment()[key];
        },
        userIsSuperUser() {
            return this.$root.user.role.id === 1 && this.$root.user.role.name === "user";
        },
        insurerContractRole() {
            return this.$root.contact_roles.find(contact_role => contact_role.name === 'Insurer');
        },
        placingBrokerContractRole() {
            return this.$root.contact_roles.find(contact_role => contact_role.name === 'Placing Broker');
        },
        producingBrokerContractRole() {
            return this.$root.contact_roles.find(contact_role => contact_role.name === 'Producing Broker');
        },
        insuredContractRole() {
            return this.$root.contact_roles.find(contact_role => contact_role.name === 'Original Insured');
        },
        userCan(permissionName) {
            return this.$root.hasOwnProperty('user') && this.$root.user.hasOwnProperty('permissions') && Boolean(this.$root.user.permissions.find(permission => permission.name === permissionName));
        },
        usingBasicActivitiesFramework() {
            return localStorage.getItem('activities-framework-type') === 'basic';
        },
        usingAdvancedActivitiesFramework() {
            return localStorage.getItem('activities-framework-type') === 'advanced';
        },
        isFeatureEnabled(feature) {
            if (this.$root.application.features.hasOwnProperty(feature) === false) {
                console.log('feature does not exists, check config xtrade: ' + feature);
                return false
            }
            return Boolean(this.$root.application.features[feature]);
        }
    },
    computed: {
        userIsInsurer() {
            return this.$root.user.party.contract_role_id === this.$root.insurerContractRole().id;
        },
        userIsPlacingBroker() {
            return this.$root.user.party.contract_role_id === this.$root.placingBrokerContractRole().id;
        },
        userIsProducingBroker() {
            return this.$root.user.party.contract_role_id === this.$root.producingBrokerContractRole().id;
        },
    }
});

/**
 * Add uuid id library for generating unique ids on the frontend.
 */
Vue.prototype.$uuid = require('uuid/v4');

//Expose for debug
window.app = app;
